import AntForm, { type AntSchema, useAntForm } from '@9troisquarts/ant-form'
import { Typography } from 'antd'
import React, { useEffect, useState } from 'react'
import { ModelDefinition } from '@9troisquarts/wand'
import axiosClient from '../../../utils/axiosClient'
import useI18n from '../../../utils/useI18n'
import { Eyebrow } from '@/react/front/components/primitives'
import useCountriesAxios from '../../../utils/hooks/useCountriesAxios'
import { hasSecondSurname, phonePlaceholders, type SupportedLocale } from '../../../utils/localeRegistry'
import { marketRequiresNationalId } from '../../../utils/marketUi'

const customerDefinition = new ModelDefinition({
  firstName: 'String',
  lastName: 'String',
  lastNameTwo: 'String',
  password: 'Password',
  passwordConfirmation: 'Password',
  birthdate: 'Date',
  email: 'String',
  gender: 'String',
  phone: 'String',
  mobilePhone: 'String',
  addressOne: 'String',
  addressTwo: 'String',
  zipCode: 'String',
  city: 'String',
  countryIso: 'String',
  dni: 'String',
  level: 'String',
  bankInformation: 'String',
  sollicitableEmail: 'Boolean',
  sollicitablePhone: 'Boolean'
})

interface SignUpProps {
  locale: SupportedLocale
}

const SignUp: React.FC<SignUpProps> = (props) => {
  const { locale } = props
  const { t } = useI18n()
  const { countriesOptions } = useCountriesAxios()
  const phonePlaceholder = phonePlaceholders(locale)

  const { object, onChange, set } = useAntForm({ email: '', sollicitableEmail: true, sollicitablePhone: true })
  const [errors, setErrors] = useState<any | undefined>(undefined)

  const createCustomer = async (customer: any): Promise<void> => {
    await axiosClient.post('/customers', {
      customer
    }).then((res) => {
      if (res === undefined) {
        window.location.reload()
      }
    }).catch(err => {
      setErrors(err.errors)
    })
  }

  const onSubmit = async (): Promise<void> => {
    const parsedData = customerDefinition.parse(object)

    if (parsedData) {
      await createCustomer(parsedData)
    }
  }

  useEffect(() => {
    // Only sync when Google Places returns a structured address object; avoid clobbering user edits with old customer defaults.
    if (!object.addressOne || typeof object.addressOne !== 'object') return

    const nextZip = object.addressOne.zipCode ?? null
    const nextCity = object.addressOne.city ?? null
    const nextCountry = object.addressOne.countryIso ?? null

    if (
      nextZip === object.zipCode &&
      nextCity === object.city &&
      nextCountry === object.countryIso
    ) return

    set({
      ...object,
      zipCode: nextZip,
      city: nextCity,
      countryIso: nextCountry
    })
  }, [object.addressOne])

  const signUpSchema: AntSchema = [
    [
      {
        name: 'gender',
        required: true,
        label: t('words.gender'),
        input: {
          type: 'select',
          options: ['female', 'male', 'not_specified'].map((gender) => ({
            label: t(`active_record.attributes.customer.genders.${gender}`),
            value: gender
          }))
        },
        colProps: {
          xs: 24,
          md: 24
        }
      },
      {
        name: 'firstName',
        required: true,
        label: t('words.first_name'),
        input: {
          type: 'string'
        },
        colProps: {
          xs: 24,
          md: 24
        }
      },
      {
        name: 'lastName',
        required: true,
        label: t('words.last_name'),
        input: {
          type: 'string'
        },
        colProps: {
          xs: 24,
          md: 24
        }
      }
    ],
    ...(hasSecondSurname(locale)
      ? [[
          {
            name: 'lastNameTwo',
            label: t('words.last_name_two'),
            input: {
              type: 'string'
            },
            colProps: {
              xs: 24,
              md: 24
            }
          }
        ]]
      : []),
    [
      {
        name: 'birthdate',
        required: true,
        label: t('words.birthdate'),
        input: {
          type: 'date',
          inputProps: {
            format: 'DD/MM/YYYY',
            showToday: false,
            placeholder: '01/01/1990',
            inputReadOnly: true
          }
        },
        colProps: {
          xs: 24,
          md: 24
        }
      },
      {
        name: 'email',
        required: true,
        label: t('words.email'),
        input: {
          type: 'string'
        },
        colProps: {
          xs: 24,
          md: 24
        }
      }
    ],
    [
      {
        name: 'password',
        required: true,
        label: t('words.password'),
        input: {
          type: 'password'
        },
        colProps: {
          xs: 24,
          md: 24
        }
      },
      {
        name: 'passwordConfirmation',
        required: true,
        label: t('words.password_confirmation'),
        input: {
          type: 'password'
        },
        colProps: {
          xs: 24,
          md: 24
        }
      }
    ],
    ...(marketRequiresNationalId()
      ? [[
          {
            name: 'dni',
            required: true,
            label: t('words.dni'),
            input: {
              type: 'string'
            },
            colProps: {
              xs: 24,
              md: 24
            }
          }
        ]]
      : []),
    [
      {
        name: 'addressOne',
        required: true,
        label: t('words.address'),
        input: {
          type: 'location',
          autocompletionRequest: {
            types: ['address']
          },
          allowFreeText: true,
          selectProps: {
            placeholder: t('sentences.street_number_and_street_name')
          },
          renderFormattedAddress: (result: any) => {
            const route = result.address_components.find((a: any) => a.types.includes('route'))?.long_name
            const streetNumber = result.address_components.find((a: any) => a.types.includes('street_number'))?.long_name
            const streetAddress = [route, streetNumber].filter(Boolean).join(' ')
            return streetAddress || result.formatted_address
          }
        },
        colProps: {
          xs: 24,
          md: 24
        }
      },
      {
        name: 'addressTwo',
        label: t('active_record.attributes.customer.address_two'),
        input: {
          type: 'string'
        },
        colProps: {
          xs: 24,
          md: 24
        }
      }
    ],
    [
      {
        name: 'zipCode',
        required: true,
        label: t('active_record.attributes.customer.zip_code'),
        input: {
          type: 'string'
        },
        colProps: {
          xs: 24,
          md: 8
        }
      },
      {
        name: 'city',
        label: t('active_record.attributes.customer.city'),
        required: true,
        input: {
          type: 'string'
        },
        colProps: {
          xs: 24,
          md: 16
        }
      }
    ],
    [
      {
        name: 'countryIso',
        label: t('active_record.attributes.customer.country'),
        required: true,
        input: {
          type: 'select',
          options: countriesOptions,
          inputProps: {
            showSearch: true
          }
        },
        colProps: {
          xs: 24,
          md: 24
        }
      }
    ],
    [
      {
        name: 'mobilePhone',
        required: true,
        label: t('words.mobile_phone'),
        input: {
          type: 'string',
          inputProps: {
            placeholder: phonePlaceholder.mobile
          }
        },
        colProps: {
          xs: 24,
          md: 24
        }
      }
    ],
    [
      {
        name: 'phone',
        label: t('words.phone_number'),
        input: {
          type: 'string',
          inputProps: {
            placeholder: phonePlaceholder.phone
          }
        },
        colProps: {
          xs: 24,
          md: 24
        }
      }
    ],
    [
      {
        name: 'sollicitablePhone',
        className: 'auth-consent-item',
        input: {
          type: 'checkbox',
          text: t('sentences.sollicitable_phone')
        },
        colProps: {
          xs: 24,
          md: 24
        }
      }
    ],
    [
      {
        name: 'sollicitableEmail',
        className: 'auth-consent-item',
        input: {
          type: 'checkbox',
          text: t('sentences.sollicitable_email')
        },
        colProps: {
          xs: 24,
          md: 24
        }
      }
    ]
  ]

  return (
    <>
      <div className='home-panel-heading'>
        <Eyebrow className='home-panel-eyebrow'>{t('sentences.auth_sign_up_eyebrow')}</Eyebrow>
        <Typography.Title level={4} className='home-panel-title'>
          {t('words.sign_up')}
        </Typography.Title>
      </div>
      <AntForm
        schema={signUpSchema}
        object={object}
        layout="vertical"
        onChange={onChange}
        onSubmit={onSubmit}
        errors={errors}
        submitText={t('words.to_sign_up')}
        actionsWrapperProps={{
          className: 'auth-form-actions'
        }}
        submitButtonProps={{
          className: 'primary-btn'
        }}
        className='auth-form'
      />
    </>
  )
}

export default SignUp
